import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import DiagnosticFormNew from "@/components/diagnostic/DiagnosticForm";

// Lazy-loaded pages (code splitting)
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const Article1 = lazy(() => import("@/pages/blog/Article1"));
const Article2 = lazy(() => import("@/pages/blog/Article2"));
const Article3 = lazy(() => import("@/pages/blog/Article3"));
const Article4 = lazy(() => import("@/pages/blog/Article4"));
const Article5 = lazy(() => import("@/pages/blog/Article5"));
const Article6 = lazy(() => import("@/pages/blog/Article6"));
const Article7 = lazy(() => import("@/pages/blog/Article7"));
const Article8 = lazy(() => import("@/pages/blog/Article8"));
const Article9 = lazy(() => import("@/pages/blog/Article9"));
const Article10 = lazy(() => import("@/pages/blog/Article10"));
const Article11 = lazy(() => import("@/pages/blog/Article11"));
const Article12 = lazy(() => import("@/pages/blog/Article12"));
const Article13 = lazy(() => import("@/pages/blog/Article13"));
const Article14 = lazy(() => import("@/pages/blog/Article14"));
const Article15 = lazy(() => import("@/pages/blog/Article15"));
const Article16 = lazy(() => import("@/pages/blog/Article16"));
const Article17 = lazy(() => import("@/pages/blog/Article17"));
const Article18 = lazy(() => import("@/pages/blog/Article18"));
const Article19 = lazy(() => import("@/pages/blog/Article19"));
const Article20 = lazy(() => import("@/pages/blog/Article20"));
import { 
  ArrowRight, 
  Check, 
  Clock, 
  Users, 
  TrendingDown, 
  AlertCircle,
  DollarSign,
  Eye,
  Target,
  Zap,
  Phone,
  ChevronRight,
  Wallet,
  HandshakeIcon,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Import Main Pages (Homepage, Sortie, Valorisation)
import { NeutralHomePage, SortieFatiguePage, ValorisationVentePage } from "@/pages/MainPages";

// Lazy-loaded SEO Landing Pages
const SEOPages = lazy(() => import("@/pages/SEOLandingPages"));
const LazyArreterConciergerie = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ArreterConciergerie })));
const LazyVendreConciergerie = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.VendreConciergerie })));
const LazySortirOperationnel = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.SortirOperationnel })));
const LazyConciergerieFatigue = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ConciergerieFatigue })));
const LazyConciergerieRentable = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ConciergerieRentable })));
const LazyCederConciergerie = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.CederConciergerie })));
const LazyReprendreConciergerie = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ReprendreConciergerie })));
const LazyProblemesConciergerie = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ProblemesConciergerie })));
const LazyConciergerieNeMarchePas = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.ConciergerieNeMarchePas })));
const LazyQuitterGestionAirbnb = lazy(() => import("@/pages/SEOLandingPages").then(m => ({ default: m.QuitterGestionAirbnb })));

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/booking/4cBuBGX5rHUXw5076Ap3";

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#FF4500] border-t-transparent rounded-full animate-spin" />
  </div>
);

// ============ HEADER COMPONENT ============
const Header = ({ onOpenForm }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`sticky-header ${scrolled ? "scrolled" : ""}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="logo">
          <img src="/logo-cledici-exit.png" alt="Cledici Exit" className="h-8" />
        </div>
        <Button 
          onClick={onOpenForm}
          className="bg-[#FF4500] hover:bg-[#E03C00] text-white font-semibold px-4 md:px-6 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-[#FF4500]/20"
          data-testid="header-cta-btn"
        >
          <span className="hidden sm:block">Démarrer le diagnostic</span>
          <span className="block sm:hidden">Diagnostic</span>
        </Button>
      </div>
    </header>
  );
};

// ============ HERO SECTION ============
const HeroSection = ({ onOpenForm }) => {
  return (
    <section className="hero-bg min-h-screen flex items-center justify-center relative" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
      
      <div className="max-w-5xl mx-auto px-4 md:px-8 text-center relative z-10 py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-[#A1A1AA] mb-8 tracking-wide uppercase">
            Pour les gérants de conciergerie
          </span>
        </motion.div>

        <motion.h1 
          className="hero-title font-black text-white mb-8 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          data-testid="hero-title"
        >
          Vous voulez arrêter votre conciergerie… mais vous ne savez pas comment faire les choses 
          <span className="text-[#FF4500]"> proprement</span> ?
        </motion.h1>

        <motion.p 
          className="text-lg md:text-xl text-[#A1A1AA] mb-12 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="hero-subtitle"
        >
          Obtenez un diagnostic gratuit, une estimation de votre situation et un plan de sortie clair en quelques minutes.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <button 
            onClick={onOpenForm}
            className="cta-button text-lg animate-pulse-glow"
            data-testid="hero-cta-btn"
          >
            Démarrer mon diagnostic gratuit
            <ArrowRight className="inline-block ml-2 w-5 h-5" />
          </button>
          <p className="mt-4 text-sm text-[#71717A]">Gratuit • Sans engagement • 3 minutes</p>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center pt-2"
        >
          <div className="w-1.5 h-3 rounded-full bg-white/40" />
        </motion.div>
      </div>
    </section>
  );
};

// ============ PROBLEMS SECTION ============
const ProblemsSection = () => {
  const problems = [
    {
      icon: Clock,
      title: "Vous êtes bloqué dans l'opérationnel",
      description: "Check-in, ménage, problèmes techniques... Vous passez vos journées à éteindre des feux.",
      large: true
    },
    {
      icon: DollarSign,
      title: "Vos revenus dépendent de vous",
      description: "Si vous vous arrêtez, tout s'arrête. Pas de revenus sans votre présence."
    },
    {
      icon: Users,
      title: "Vos équipes sont instables",
      description: "Turn-over constant, formation permanente, qualité variable."
    },
    {
      icon: TrendingDown,
      title: "Votre croissance est plafonnée",
      description: "Impossible de scaler sans sacrifier votre santé ou votre qualité."
    },
    {
      icon: AlertCircle,
      title: "Vous pensez arrêter",
      description: "Mais vous ne savez pas par où commencer ni comment ne pas tout perdre."
    }
  ];

  return (
    <section className="section-padding bg-[#050505]" data-testid="problems-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">La réalité</span>
          <h2 className="section-title font-bold text-white mt-4">
            Vous vous reconnaissez ?
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`bento-card ${problem.large ? 'md:col-span-8' : 'md:col-span-4'}`}
              data-testid={`problem-card-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center mb-6">
                <problem.icon className="w-6 h-6 text-[#FF4500]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{problem.title}</h3>
              <p className="text-[#A1A1AA] leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ AUTHORITY SECTION ============
const AuthoritySection = () => {
  const stats = [
    { value: "92%", label: "Taux de rétention propriétaires" },
    { value: "270+", label: "Logements repris et gérés" },
    { value: "15+", label: "Personnes dans l'équipe" }
  ];

  const testimonials = [
    {
      quote: "J'ai travaillé 4 ans non-stop, 7j/7. Cledici a repris mes 32 logements en 6 semaines. Aujourd'hui je touche un revenu passif et j'ai retrouvé ma vie.",
      name: "Sophie M.",
      role: "28 logements, Lyon",
      tunnel: "Revenu passif"
    },
    {
      quote: "Je pensais que ma conciergerie ne valait rien parce qu'elle était déficitaire. Gaël et Nicolas m'ont montré que mes contrats avaient de la valeur. Rachat bouclé en 45 jours.",
      name: "Marc D.",
      role: "45 logements, Bordeaux",
      tunnel: "Rachat structuré"
    },
    {
      quote: "Le plus dur c'était l'idée d'abandonner mes propriétaires. La transition a été impeccable, tous sont restés. Je recommande les yeux fermés.",
      name: "Julien R.",
      role: "18 logements, Nice",
      tunnel: "Rachat structuré"
    }
  ];

  return (
    <section className="section-padding bg-[#0A0A0A] relative overflow-hidden" data-testid="authority-section">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF4500]/[0.02] to-transparent" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">Qui sommes-nous</span>
          <h2 className="section-title font-bold text-white mt-4 mb-6">
            Des opérateurs, pas des consultants
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            On ne vous donne pas des conseils. On rachète et on exploite des conciergeries. C'est notre métier au quotidien.
          </p>
        </motion.div>

        {/* Founders */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col md:flex-row items-center gap-10 md:gap-16 mb-20"
          data-testid="authority-founders"
        >
          <div className="w-full md:w-2/5 flex-shrink-0">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://customer-assets.emergentagent.com/job_ac335dbd-d033-4455-9009-be3eb6ed9619/artifacts/i9km8emq_Deux_fondateurs_Cledici_202603211253.jpeg"
                alt="Gaël Pélerin et Nicolas Moreau, co-fondateurs de Cledici Exit"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex gap-6">
                  <div>
                    <p className="text-white font-semibold text-sm">Gaël Pélerin</p>
                    <p className="text-white/60 text-xs">Co-fondateur</p>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Nicolas Moreau</p>
                    <p className="text-white/60 text-xs">Co-fondateur</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-3/5">
            <blockquote className="text-xl md:text-2xl font-light text-white leading-relaxed mb-6" data-testid="authority-quote">
              "La majorité des conciergeries ne sont ni structurées pour être revendues, ni pour fonctionner sans leur fondateur. On a créé Cledici Exit pour changer ça."
            </blockquote>
            <p className="text-[#A1A1AA] leading-relaxed mb-6">
              Anciens gérants de conciergerie nous-mêmes, on connaît le quotidien : les appels à 23h, les ménages ratés, les propriétaires exigeants. On a construit une structure capable de reprendre et d'améliorer n'importe quel portefeuille — pour que vous puissiez passer à autre chose, sereinement.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-[2px] bg-[#FF4500]" />
              <span className="text-[#FF4500] text-sm font-medium">Gaël & Nicolas, co-fondateurs</span>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-3 gap-4 md:gap-8 mb-20"
          data-testid="authority-stats"
        >
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center py-8 px-4 rounded-2xl bg-[#111111] border border-white/5"
              data-testid={`stat-${index}`}
            >
              <p className="text-3xl md:text-5xl font-bold text-[#FF4500] mb-2">{stat.value}</p>
              <p className="text-[#A1A1AA] text-xs md:text-sm">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
          data-testid="authority-testimonials"
        >
          <h3 className="text-center text-lg font-semibold text-white mb-10">
            Ils ont fait le pas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-[#111111] border border-white/5 flex flex-col"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-[#FF4500]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#A1A1AA] text-sm leading-relaxed flex-1 mb-5">"{t.quote}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div>
                    <p className="text-white font-medium text-sm">{t.name}</p>
                    <p className="text-[#52525B] text-xs">{t.role}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#FF4500]/60 bg-[#FF4500]/5 px-2 py-1 rounded">{t.tunnel}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Team photo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative rounded-2xl overflow-hidden"
          data-testid="authority-team"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_ac335dbd-d033-4455-9009-be3eb6ed9619/artifacts/017wipi6_%C3%89quipe_fin_s%C3%A9minaire_202603211253.jpeg"
            alt="L'équipe Cledici Exit au complet"
            className="w-full h-64 md:h-80 object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-center">
            <p className="text-white text-lg md:text-xl font-semibold mb-2">
              Une équipe de +15 opérationnels terrain
            </p>
            <p className="text-white/60 text-sm max-w-xl mx-auto">
              City managers, responsables ménage, revenue managers, support propriétaires — chaque reprise est portée par une équipe dédiée, pas par un seul individu.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ SOLUTION SECTION ============
const SolutionSection = ({ onOpenForm }) => {
  const scenarios = [
    {
      icon: HandshakeIcon,
      title: "Rachat structuré",
      subtitle: "On rachète votre conciergerie",
      description: "Vous vendez, on achète. Paiement structuré : cash + earn-out. Vous passez à autre chose sereinement.",
      features: ["Offre sous 48h", "Paiement structuré sécurisé", "Transition accompagnée"]
    },
    {
      icon: Wallet,
      title: "Revenu passif",
      subtitle: "Vos contrats passent chez nous",
      description: "On reprend toute l'exploitation. Vous touchez une commission récurrente basée sur la performance. Zéro gestion.",
      features: ["Revenus récurrents sans effort", "Aucune charge opérationnelle", "Commission basée sur la performance"]
    }
  ];

  return (
    <section className="section-padding bg-[#050505]" data-testid="solution-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">La solution</span>
          <h2 className="section-title font-bold text-white mt-4 mb-6">
            Sortir intelligemment de votre conciergerie
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Selon votre situation, nous vous aidons à trouver la meilleure option pour vous.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
          {scenarios.map((scenario, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="scenario-card"
              data-testid={`scenario-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-[#FF4500]/10 flex items-center justify-center mb-6">
                <scenario.icon className="w-7 h-7 text-[#FF4500]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{scenario.title}</h3>
              <p className="text-[#FF4500] text-sm font-medium mb-4">{scenario.subtitle}</p>
              <p className="text-[#A1A1AA] mb-6 leading-relaxed">{scenario.description}</p>
              <ul className="space-y-3">
                {scenario.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#A1A1AA]">
                    <Check className="w-4 h-4 text-[#FF4500] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
        >
          <button 
            onClick={onOpenForm}
            className="cta-button text-lg"
            data-testid="solution-cta-btn"
          >
            Découvrir ma meilleure option
            <ChevronRight className="inline-block ml-2 w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// ============ BENEFITS SECTION ============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Eye,
      title: "Vision claire de votre situation",
      description: "Comprenez exactement où vous en êtes et quelles sont vos options réelles."
    },
    {
      icon: DollarSign,
      title: "Estimation de la valeur",
      description: "Découvrez combien vaut réellement votre activité sur le marché actuel."
    },
    {
      icon: Target,
      title: "Plan de sortie personnalisé",
      description: "Un roadmap adapté à votre situation, vos objectifs et votre timeline."
    },
    {
      icon: Zap,
      title: "Actions concrètes immédiates",
      description: "Repartez avec des étapes claires à mettre en place dès demain."
    }
  ];

  return (
    <section className="section-padding bg-[#0A0A0A]" data-testid="benefits-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">Ce que vous obtenez</span>
          <h2 className="section-title font-bold text-white mt-4">
            En quelques minutes, vous aurez :
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="benefit-item"
              data-testid={`benefit-item-${index}`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0">
                <benefit.icon className="w-6 h-6 text-[#FF4500]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-[#A1A1AA]">{benefit.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ CTA SECTION ============
const CTASection = ({ onOpenForm }) => {
  return (
    <section className="section-padding bg-[#050505] relative overflow-hidden" data-testid="cta-section">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FF4500]/5 blur-3xl" />
      </div>
      
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title font-bold text-white mb-6">
            Prêt à reprendre le contrôle ?
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-10 max-w-xl mx-auto">
            Le diagnostic est gratuit, sans engagement, et prend moins de 3 minutes. Vous méritez une sortie à la hauteur de votre investissement.
          </p>
          <button 
            onClick={onOpenForm}
            className="cta-button text-lg animate-pulse-glow"
            data-testid="final-cta-btn"
          >
            Démarrer mon diagnostic gratuit
            <ArrowRight className="inline-block ml-2 w-5 h-5" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// ============ FAQ SECTION ============
const FAQSection = () => {
  const faqs = [
    {
      question: "Comment vendre sa conciergerie Airbnb ?",
      answer: "Vendre une conciergerie Airbnb nécessite d'abord une valorisation précise de votre portefeuille (nombre de mandats, CA mensuel, rentabilité). Chez Cledici Exit, on simplifie le processus : vous soumettez votre dossier, on l'étudie et on vous fait une offre de rachat directe sous 48h. Paiement structuré (cash + earn-out), transfert des contrats accompagné, transition fluide. Pas besoin de chercher un acheteur : on est l'acheteur."
    },
    {
      question: "Combien vaut ma conciergerie ?",
      answer: "La valorisation d'une conciergerie se base sur l'EBE (Excédent Brut d'Exploitation), pas sur le chiffre d'affaires. L'EBE est ensuite multiplié par un coefficient (entre 1x et 4x) qui dépend de 4 piliers : la dépendance au gérant, les process documentés, la sécurité des contrats et le moteur d'acquisition. Notre diagnostic gratuit vous donne une première estimation en 3 minutes."
    },
    {
      question: "Puis-je arrêter ma conciergerie sans tout perdre ?",
      answer: "Absolument. C'est même notre spécialité. Trop de gérants abandonnent leur activité en perdant la valeur qu'ils ont créée. Nous proposons 2 options : on rachète directement votre conciergerie (paiement structuré : cash + earn-out), ou vos contrats passent chez nous et vous touchez une commission récurrente basée sur la performance — du revenu passif, sans aucune gestion."
    },
    {
      question: "Qu'est-ce qu'un revenu passif avec ma conciergerie ?",
      answer: "Au lieu de vendre, vos contrats de gestion passent chez Cledici. On reprend 100% de l'exploitation. Vous recevez une commission mensuelle basée sur la performance réelle du parc (généralement 10-20% du CA). Zéro charge de travail, zéro gestion quotidienne. C'est idéal si vous voulez arrêter de travailler dans votre conciergerie tout en gardant un revenu récurrent."
    },
    {
      question: "Comment fonctionne le revenu passif avec Cledici Exit ?",
      answer: "C'est simple : vos contrats de gestion passent chez Cledici. On reprend 100% de l'exploitation (check-in, ménage, voyageurs, maintenance). Vous touchez une commission récurrente basée sur la performance réelle du parc. C'est idéal si vous voulez arrêter de gérer tout en conservant un revenu mensuel sans effort."
    },
    {
      question: "Quels sont les signes qu'il faut sortir de sa conciergerie ?",
      answer: "Les signaux d'alarme sont clairs : vous travaillez plus de 50h/semaine, vos revenus stagnent malgré l'effort, vous n'arrivez pas à prendre de vacances, votre équipe change constamment, vous redoutez les appels de voyageurs. Si vous cochez 2 ou 3 de ces cases, il est temps d'envisager une sortie intelligente."
    },
    {
      question: "Combien de temps dure le processus ?",
      answer: "Pour un rachat : vous soumettez votre dossier, on l'étudie et on revient avec une offre sous 48h. La transition complète (transfert des mandats, mise en place opérationnelle) prend généralement 30 à 90 jours selon la taille du portefeuille. Pour le revenu passif : la bascule opérationnelle peut se faire en 30 à 60 jours."
    },
    {
      question: "Mes contrats propriétaires sont-ils transférables ?",
      answer: "Dans la majorité des cas, oui. La clé est d'avoir des contrats bien rédigés avec une clause de cession. Même sans clause explicite, la plupart des propriétaires acceptent le transfert quand le repreneur (nous, en l'occurrence) présente des garanties solides et une continuité de service. On s'occupe de chaque transfert et on sécurise juridiquement la transition."
    },
    {
      question: "Pourquoi Cledici Exit plutôt qu'un courtier classique ?",
      answer: "Parce qu'on ne vous aide pas à chercher un acheteur. On EST l'acheteur. Pas de mise sur le marché, pas d'attente, pas d'incertitude. Vous soumettez votre dossier, on l'étudie, et on vous fait une offre directe sous 48h. Et si le rachat ne vous convient pas, on vous propose l'option revenu passif : vos contrats passent chez nous, vous touchez une commission chaque mois."
    },
    {
      question: "Le diagnostic est-il vraiment gratuit ?",
      answer: "Oui, 100% gratuit et sans engagement. En 3 minutes, vous obtenez une vision claire de votre situation, une première estimation de la valeur de votre activité, et les options de sortie adaptées à votre cas. Si vous souhaitez aller plus loin, un expert vous contacte pour approfondir votre projet."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="section-padding bg-[#0A0A0A]" data-testid="faq-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FF4500] text-sm tracking-[0.2em] uppercase font-medium">Questions fréquentes</span>
          <h2 className="section-title font-bold text-white mt-4 mb-6">
            Tout ce que vous devez savoir
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Les réponses aux questions que se posent les gérants de conciergerie avant de passer à l'action.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-[#111111] border border-white/10 rounded-xl px-6 overflow-hidden data-[state=open]:border-[#FF4500]/30"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-white text-left text-base md:text-lg font-medium py-5 hover:no-underline hover:text-[#FF4500] transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#A1A1AA] leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center mt-12 p-8 bg-[#111111] border border-white/10 rounded-2xl"
        >
          <p className="text-[#A1A1AA] mb-4">Vous avez une autre question ?</p>
          <a 
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#FF4500] font-semibold hover:underline"
            data-testid="faq-contact-link"
          >
            Parlez directement à un expert
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ============ FOOTER ============
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5 py-16 px-4" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo-cledici-exit.png" alt="Cledici Exit" className="h-8" />
            </div>
            <p className="text-sm text-[#71717A] leading-relaxed">
              Rachat et reprise de conciergeries Airbnb. Sortez intelligemment de votre activité.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Votre situation</h4>
            <ul className="space-y-2">
              <li><a href="/sortie" className="text-sm text-[#71717A] hover:text-white transition-colors">Sortie opérationnelle</a></li>
              <li><a href="/valorisation" className="text-sm text-[#71717A] hover:text-white transition-colors">Rachat & Revenu passif</a></li>
              <li><a href="/conciergerie-airbnb-fatigue" className="text-sm text-[#71717A] hover:text-white transition-colors">Fatigue & épuisement</a></li>
              <li><a href="/conciergerie-airbnb-rentable" className="text-sm text-[#71717A] hover:text-white transition-colors">Rentabilité en question</a></li>
              <li><a href="/problemes-conciergerie-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Problèmes courants</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Solutions</h4>
            <ul className="space-y-2">
              <li><a href="/vendre-sa-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Vendre sa conciergerie</a></li>
              <li><a href="/ceder-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Céder sa conciergerie</a></li>
              <li><a href="/arreter-conciergerie-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Arrêter sa conciergerie</a></li>
              <li><a href="/sortir-operationnel-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Sortir de l'opérationnel</a></li>
              <li><a href="/quitter-gestion-airbnb" className="text-sm text-[#71717A] hover:text-white transition-colors">Quitter la gestion Airbnb</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Informations</h4>
            <ul className="space-y-2">
              <li><a href="/blog" className="text-sm text-[#FF4500] hover:text-white transition-colors">Blog</a></li>
              <li><a href="/reprendre-conciergerie" className="text-sm text-[#71717A] hover:text-white transition-colors">Reprendre une conciergerie</a></li>
              <li><a href="/conciergerie-airbnb-ne-marche-pas" className="text-sm text-[#71717A] hover:text-white transition-colors">Conciergerie qui ne marche pas</a></li>
              <li><a href="#" className="text-sm text-[#71717A] hover:text-white transition-colors">Mentions légales</a></li>
              <li><a href="#" className="text-sm text-[#71717A] hover:text-white transition-colors">Politique de confidentialité</a></li>
              <li><a href="#" className="text-sm text-[#71717A] hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-[#71717A]">© 2025 Cledici Exit. Tous droits réservés.</p>
          <p className="text-xs text-[#52525B]">Rachat de conciergeries Airbnb partout en France</p>
        </div>
      </div>
    </footer>
  );
};

// ============ MAIN APP ============
const HomePage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />
      
      <Header onOpenForm={() => setIsFormOpen(true)} />
      
      <main>
        <HeroSection onOpenForm={() => setIsFormOpen(true)} />
        <ProblemsSection />
        <AuthoritySection />
        <SolutionSection onOpenForm={() => setIsFormOpen(true)} />
        <BenefitsSection />
        <FAQSection />
        <CTASection onOpenForm={() => setIsFormOpen(true)} />
      </main>

      <Footer />

      <DiagnosticFormNew 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
      />
    </div>
  );
};

// Page Wrapper with Diagnostic Form
const PageWrapper = ({ PageComponent }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  return (
    <>
      <PageComponent onOpenForm={() => setIsFormOpen(true)} />
      <DiagnosticFormNew 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
      />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Main Pages with Segmentation */}
          <Route path="/" element={<PageWrapper PageComponent={NeutralHomePage} />} />
          <Route path="/sortie" element={<PageWrapper PageComponent={SortieFatiguePage} />} />
          <Route path="/valorisation" element={<PageWrapper PageComponent={ValorisationVentePage} />} />
          
          {/* Blog */}
          <Route path="/blog" element={<PageWrapper PageComponent={BlogIndex} />} />
          <Route path="/blog/combien-vaut-conciergerie-airbnb" element={<PageWrapper PageComponent={Article1} />} />
          <Route path="/blog/ebe-conciergerie-calcul-valorisation" element={<PageWrapper PageComponent={Article2} />} />
          <Route path="/blog/dependance-gerant-conciergerie" element={<PageWrapper PageComponent={Article3} />} />
          <Route path="/blog/process-documentes-conciergerie" element={<PageWrapper PageComponent={Article4} />} />
          <Route path="/blog/securite-contrats-conciergerie" element={<PageWrapper PageComponent={Article5} />} />
          <Route path="/blog/moteur-acquisition-conciergerie" element={<PageWrapper PageComponent={Article6} />} />
          <Route path="/blog/earn-out-cession-conciergerie" element={<PageWrapper PageComponent={Article7} />} />
          <Route path="/blog/erreurs-vente-conciergerie" element={<PageWrapper PageComponent={Article8} />} />
          <Route path="/blog/burnout-conciergerie-airbnb" element={<PageWrapper PageComponent={Article9} />} />
          <Route path="/blog/revenu-passif-conciergerie" element={<PageWrapper PageComponent={Article10} />} />
          <Route path="/blog/conciergerie-airbnb-deficitaire-continuer-arreter" element={<PageWrapper PageComponent={Article11} />} />
          <Route path="/blog/gerant-conciergerie-epuise-options" element={<PageWrapper PageComponent={Article12} />} />
          <Route path="/blog/combien-temps-vendre-conciergerie" element={<PageWrapper PageComponent={Article13} />} />
          <Route path="/blog/conciergerie-partir-sans-perdre-proprietaires" element={<PageWrapper PageComponent={Article14} />} />
          <Route path="/blog/reprendre-conciergerie-ce-que-regarde-repreneur" element={<PageWrapper PageComponent={Article15} />} />
          <Route path="/blog/conciergerie-50-logements-pourquoi-bloque" element={<PageWrapper PageComponent={Article16} />} />
          <Route path="/blog/deleguer-conciergerie-tiers-mode-emploi" element={<PageWrapper PageComponent={Article17} />} />
          <Route path="/blog/salarie-entrepreneur-conciergerie-quand-job-depasse-business" element={<PageWrapper PageComponent={Article18} />} />
          <Route path="/blog/vendre-conciergerie-sans-courtier" element={<PageWrapper PageComponent={Article19} />} />
          <Route path="/blog/conciergerie-perte-vitesse-diagnostic-plan-action" element={<PageWrapper PageComponent={Article20} />} />
          
          {/* SEO Landing Pages */}
          <Route path="/arreter-conciergerie-airbnb" element={<PageWrapper PageComponent={LazyArreterConciergerie} />} />
          <Route path="/vendre-sa-conciergerie" element={<PageWrapper PageComponent={LazyVendreConciergerie} />} />
          <Route path="/sortir-operationnel-conciergerie" element={<PageWrapper PageComponent={LazySortirOperationnel} />} />
          <Route path="/conciergerie-airbnb-fatigue" element={<PageWrapper PageComponent={LazyConciergerieFatigue} />} />
          <Route path="/conciergerie-airbnb-rentable" element={<PageWrapper PageComponent={LazyConciergerieRentable} />} />
          <Route path="/ceder-conciergerie" element={<PageWrapper PageComponent={LazyCederConciergerie} />} />
          <Route path="/reprendre-conciergerie" element={<PageWrapper PageComponent={LazyReprendreConciergerie} />} />
          <Route path="/problemes-conciergerie-airbnb" element={<PageWrapper PageComponent={LazyProblemesConciergerie} />} />
          <Route path="/conciergerie-airbnb-ne-marche-pas" element={<PageWrapper PageComponent={LazyConciergerieNeMarchePas} />} />
          <Route path="/quitter-gestion-airbnb" element={<PageWrapper PageComponent={LazyQuitterGestionAirbnb} />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
